// Prisoversikt for kassa (A4): det kunden skal betale for varene på en ordre eller et skiplukk,
// til å ta med i kassa ved henting. Som servicefakturaen i WorkshopDetails har varer med EAN en
// QR-kode kassa kan skanne. Ikke et salgsbilag: kvitteringen lages i kassa ved betaling (P4).
// Alt fra brukere og databasen escapes (#30); kostpris og dekningsbidrag skrives aldri ut.
import * as QR from 'qrcode';
import { escapeHtml } from '../utils/format';
import { linjeTall, oppsummering, kr } from './kalkyle';
import { butikkFor } from './butikker';
import { varekodeSifre, varekodeTekst } from './varekode';

const e = (v) => escapeHtml(v);
// EAN eller PLU (varekode.js); begge får QR-kode kassa kan skanne.
const eanSifre = (l) => varekodeSifre(l?.ean);

async function qrFor(linjer) {
  return Promise.all(linjer.map(async (l) => {
    const ean = eanSifre(l);
    if (!ean) return null;
    try {
      return await QR.toDataURL(ean, { margin: 1, scale: 4 });
    } catch {
      return null;
    }
  }));
}

// kilde: { tilbudRef, godkjent } når prisene er fra et godkjent pristilbud, eller { tekst } for en
// egen linje (pristilbud skrevet ut direkte fra /offer-details).
// ordre.type: 'ordre' | 'skiplukk' | 'tilbud' (da er ordreid tilbudets ref.).
// klubb: { klubbNavn } når saken er en klubbestilling (src/lib/klubbKontakt.js); da er kunde
// kontaktpersonen i klubben.
export function prisoversiktHtml({ ordre, kunde, klubb = null, linjer, kilde, qr = [], utskrevet = new Date() }) {
  const butikk = butikkFor(ordre?.butikkid);
  const sum = oppsummering(linjer);
  const skiplukk = ordre?.type === 'skiplukk';
  const tilbud = ordre?.type === 'tilbud';
  const etikett = skiplukk ? 'Skiplukk' : tilbud ? 'Pristilbud' : 'Ordre';
  const kundeNavn = [kunde?.firstName, kunde?.lastName].filter(Boolean).join(' ');
  const dato = (d) => new Date(d).toLocaleDateString('nb-NO');
  const harRabatt = linjer.some((l) => linjeTall(l).rabattKr > 0);

  const rader = linjer.map((l, i) => {
    const t = linjeTall(l);
    const antall = Math.max(1, Math.round(Number(l.antall) || 1));
    return `<tr>
      <td><strong>${e(l.artikkel || 'Vare')}</strong>${varekodeTekst(l.ean) ? `<div class="muted">${e(varekodeTekst(l.ean))}</div>` : ''}</td>
      <td class="qr">${qr[i] ? `<img src="${qr[i]}" alt="">` : ''}</td>
      <td class="h">${e(antall)}</td>
      <td class="h">${e(kr(Number(String(l.pris ?? '').replace(',', '.')) || 0))}</td>
      ${harRabatt ? `<td class="h">${t.rabattKr > 0 ? e(kr(-t.rabattKr)) : ''}</td>` : ''}
      <td class="h"><strong>${e(kr(t.total))}</strong></td>
    </tr>`;
  }).join('');

  return `<!doctype html>
<html lang="no">
<head>
<meta charset="utf-8">
<title>Prisoversikt – ${etikett.toLowerCase()} ${e(ordre?.ordreid ?? '')}</title>
<style>
  @page { size: A4; margin: 14mm; }
  * { box-sizing: border-box; }
  body { font-family: Arial, Helvetica, sans-serif; color: #111; font-size: 11pt; margin: 0; }
  header { display: flex; justify-content: space-between; align-items: flex-start; gap: 12pt; border-bottom: 2px solid #111; padding-bottom: 8pt; margin-bottom: 10pt; }
  h1 { font-size: 20pt; margin: 0; }
  .selger { margin-top: 3pt; color: #333; font-size: 9.5pt; }
  .nrboks { border: 2px solid #111; border-radius: 6pt; padding: 6pt 10pt; text-align: right; }
  .nrboks .etikett { font-size: 8.5pt; font-weight: bold; text-transform: uppercase; letter-spacing: .03em; }
  .nrboks .nr { font-family: Menlo, Consolas, monospace; font-size: 22pt; font-weight: bold; }
  .muted { color: #555; font-size: 9pt; }
  .info { display: flex; gap: 24pt; margin-bottom: 10pt; }
  .info div { flex: 1; }
  .info strong { display: block; font-size: 9pt; text-transform: uppercase; color: #333; }
  .kilde { border: 1px solid #999; border-radius: 4pt; padding: 5pt 8pt; margin-bottom: 10pt; }
  table { width: 100%; border-collapse: collapse; }
  th, td { padding: 5pt 4pt; border-bottom: 1px solid #ccc; text-align: left; vertical-align: middle; }
  th { background: #f2f2f2; font-size: 9.5pt; }
  .h { text-align: right; white-space: nowrap; }
  .qr { width: 18mm; }
  .qr img { width: 16mm; height: 16mm; image-rendering: pixelated; }
  tr { break-inside: avoid; page-break-inside: avoid; }
  .total { margin-top: 10pt; margin-left: auto; width: 60%; border: 2px solid #111; border-radius: 6pt; padding: 8pt 10pt; display: flex; justify-content: space-between; align-items: baseline; }
  .total .belop { font-size: 20pt; font-weight: bold; }
  .rabatt { margin-left: auto; width: 60%; text-align: right; margin-top: 4pt; }
  footer { margin-top: 14pt; font-size: 9.5pt; color: #333; border-top: 1px solid #999; padding-top: 6pt; }
</style>
</head>
<body>
  <header>
    <div>
      <h1>Prisoversikt – til kassa</h1>
      <div class="selger">${e(butikk.selskap)} · org.nr. ${e(butikk.orgnr)} · ${e(butikk.adresse)} · ${e(butikk.telefon)}</div>
      <div class="muted">Skrevet ut ${e(utskrevet.toLocaleString('nb-NO', { day: 'numeric', month: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' }))}</div>
    </div>
    <div class="nrboks">
      <div class="etikett">${etikett}</div>
      <div class="nr">${e(ordre?.ordreid ?? '')}</div>
    </div>
  </header>

  <div class="info">
    ${klubb?.klubbNavn ? `<div><strong>Klubb</strong>${e(klubb.klubbNavn)}</div>` : ''}
    <div><strong>${klubb ? 'Kontaktperson' : 'Kunde'}</strong>${e(kundeNavn || '–')}<br>${e(kunde?.phoneNumber || '')}</div>
    <div><strong>Hentes i</strong>${e(butikk.navn)}</div>
  </div>

  ${kilde?.tekst ? `<div class="kilde">${e(kilde.tekst)}</div>` : ''}
  ${kilde && !kilde.tekst ? `<div class="kilde">I henhold til godkjent pristilbud${kilde.tilbudRef ? ` #${e(kilde.tilbudRef)}` : ''}${kilde.godkjent ? `, godkjent av kunden ${e(dato(kilde.godkjent))}` : ''}.</div>` : ''}

  <table>
    <thead><tr><th>Vare</th><th class="qr">Skann</th><th class="h">Antall</th><th class="h">Pris</th>${harRabatt ? '<th class="h">Rabatt</th>' : ''}<th class="h">Sum</th></tr></thead>
    <tbody>${rader}</tbody>
  </table>

  ${sum.rabattKr > 0 ? `<div class="rabatt">Rabatt totalt: ${e(kr(sum.rabattKr))}</div>` : ''}
  <div class="total"><span>Å betale</span><span class="belop">${e(kr(sum.total))}</span></div>

  <footer>Betales i kassa ved henting. Alle priser er inkl. 25 % mva. Dette er en prisoversikt, ikke en kvittering: kvitteringen får du i kassa.</footer>
</body>
</html>`;
}

// Lager QR-kodene, åpner utskriftsvinduet og skriver ut. Gir false hvis nettleseren stoppet vinduet.
export async function skrivUtPrisoversikt({ ordre, kunde, klubb = null, linjer, kilde }) {
  const vindu = window.open('', '', 'width=900,height=1100');
  if (!vindu) return false;
  const qr = await qrFor(linjer);
  vindu.document.write(prisoversiktHtml({ ordre, kunde, klubb, linjer, kilde, qr }));
  vindu.document.close();
  vindu.focus();
  vindu.onafterprint = () => vindu.close();
  // Vent til QR-bildene er tegnet før utskrift.
  setTimeout(() => vindu.print(), 300);
  return true;
}
